import type {
  CategorySubScore,
  JudgeQueueRow,
  RubricCategoryRow,
  RubricWeightRow,
} from "../types/database";
import { supabase } from "./supabase";

type RawQueueSubmission = {
  id: string;
  deliverable_id: string;
  team_id: string;
  file_url: string;
  file_name: string;
  file_size_bytes: number | null;
  deliverables:
    | { name: string; sequence: number; is_locked_gate: boolean; rubric: unknown | null }
    | { name: string; sequence: number; is_locked_gate: boolean; rubric: unknown | null }[]
    | null;
  teams: { name: string } | { name: string }[] | null;
};

type RawScore = {
  submission_id: string;
  score: number | null;
  passed_gate: boolean | null;
  rubric_notes: string | null;
};

export type JudgeCompetitionSummary = {
  id: string;
  name: string;
  brief: string | null;
};

function unwrapOne<T>(rel: T | T[] | null | undefined): T | null {
  if (rel == null) return null;
  return Array.isArray(rel) ? (rel[0] ?? null) : rel;
}

export async function getJudgeQueue(competitionId: string): Promise<JudgeQueueRow[]> {
  const { data: judgeData, error: judgeErr } = await supabase.rpc("judge_id_for", { comp_id: competitionId });
  if (judgeErr || !judgeData) return [];
  const judgeId = String(judgeData);

  const [{ data: subData, error: subErr }, { data: scoreData }] = await Promise.all([
    supabase
      .from("submissions")
      .select("id, deliverable_id, team_id, file_url, file_name, file_size_bytes, deliverables(name, sequence, is_locked_gate, rubric), teams(name)")
      .eq("competition_id", competitionId),
    supabase
      .from("submission_scores")
      .select("submission_id, score, passed_gate, rubric_notes")
      .eq("competition_id", competitionId)
      .eq("judge_id", judgeId),
  ]);

  if (subErr || !subData) return [];
  const scoreBySubmission = new Map<string, RawScore>();
  for (const s of (scoreData ?? []) as RawScore[]) {
    scoreBySubmission.set(s.submission_id, s);
  }

  const out = (subData as RawQueueSubmission[]).map((row) => {
    const del = unwrapOne(row.deliverables);
    const team = unwrapOne(row.teams);
    const mine = scoreBySubmission.get(row.id);
    return {
      submission_id: row.id,
      deliverable_id: row.deliverable_id,
      deliverable_name: del?.name ?? "",
      deliverable_sequence: del?.sequence ?? 0,
      deliverable_is_locked_gate: del?.is_locked_gate ?? false,
      deliverable_rubric: del?.rubric ?? null,
      team_id: row.team_id,
      team_name: team?.name ?? "Team",
      file_url: row.file_url,
      file_name: row.file_name,
      file_size_bytes: row.file_size_bytes,
      my_score: mine?.score ?? null,
      my_passed_gate: mine?.passed_gate ?? null,
      my_rubric_notes: mine?.rubric_notes ?? null,
    } satisfies JudgeQueueRow;
  });

  out.sort((a, b) => {
    const aPending = a.my_score == null ? 0 : 1;
    const bPending = b.my_score == null ? 0 : 1;
    if (aPending !== bPending) return aPending - bPending;
    return a.deliverable_sequence - b.deliverable_sequence || a.team_name.localeCompare(b.team_name);
  });
  return out;
}

export async function getJudgeCompetitionSummary(competitionId: string): Promise<JudgeCompetitionSummary | null> {
  const { data, error } = await supabase
    .from("competitions")
    .select("id, name, brief")
    .eq("id", competitionId)
    .maybeSingle();
  if (error || !data) return null;
  return data as JudgeCompetitionSummary;
}

export async function getSubmissionDownloadUrl(filePath: string): Promise<{ url: string | null; error: Error | null }> {
  const { data, error } = await supabase.storage.from("submissions").createSignedUrl(filePath, 3600);
  if (error) return { url: null, error: new Error(error.message) };
  return { url: data?.signedUrl ?? null, error: null };
}

// ─── Rubric categories + weights ──────────────────────────────────────

export async function listRubricCategories(): Promise<RubricCategoryRow[]> {
  const { data, error } = await supabase
    .from("rubric_categories")
    .select("id, slug, name, description, display_order")
    .eq("is_active", true)
    .order("display_order", { ascending: true });
  if (error || !data) return [];
  return data as RubricCategoryRow[];
}

export async function getCompetitionRubricWeights(competitionId: string): Promise<RubricWeightRow[]> {
  // Fetch categories + this comp's weights, hydrate into one row per category
  // (display_order handles the canonical ordering even if a weight is missing).
  const [{ data: cats }, { data: weights }] = await Promise.all([
    supabase
      .from("rubric_categories")
      .select("id, slug, name, description, display_order")
      .eq("is_active", true)
      .order("display_order", { ascending: true }),
    supabase
      .from("competition_rubric_weights")
      .select("category_id, weight_pct")
      .eq("competition_id", competitionId),
  ]);

  const catRows = (cats ?? []) as RubricCategoryRow[];
  const wMap = new Map<string, number>();
  for (const w of (weights ?? []) as { category_id: string; weight_pct: number }[]) {
    wMap.set(w.category_id, w.weight_pct);
  }
  return catRows.map((c) => ({ ...c, weight_pct: wMap.get(c.id) ?? 0 }));
}

export type SetRubricWeightsInput = {
  competitionId: string;
  weights: { category_id: string; weight_pct: number }[];
  force?: boolean;
};

export async function setCompetitionRubricWeights(
  input: SetRubricWeightsInput
): Promise<{ error: Error | null }> {
  const { error } = await supabase.rpc("set_competition_rubric_weights", {
    p_competition_id: input.competitionId,
    p_weights: input.weights,
    p_force: input.force ?? false,
  });
  return { error: error ? new Error(error.message) : null };
}

export async function competitionHasSubmittedScores(competitionId: string): Promise<boolean> {
  const { count } = await supabase
    .from("submission_scores")
    .select("id", { count: "exact", head: true })
    .eq("competition_id", competitionId)
    .eq("status", "submitted");
  return (count ?? 0) > 0;
}

// ─── Per-submission assignment for the score page ──────────────────────

export type JudgeAssignmentDetail = {
  submission: {
    id: string;
    file_url: string;
    file_name: string;
    file_size_bytes: number | null;
    submitted_at: string | null;
    signed_url: string | null;
  };
  team: {
    id: string;
    name: string;
    members: { student_id: string; full_name: string | null; avatar_url: string | null }[];
    other_submissions: {
      submission_id: string;
      deliverable_id: string;
      deliverable_name: string;
      deliverable_sequence: number;
    }[];
  };
  deliverable: {
    id: string;
    name: string;
    sequence: number;
    is_locked_gate: boolean;
    rubric: string | null;
    point_value: number | null;
    due_at: string | null;
    file_type: string | null;
  };
  competition: {
    id: string;
    name: string;
    brief: string | null;
    opens_at: string | null;
    closes_at: string | null;
    company_name: string | null;
  };
  myScore: {
    score: number | null;
    passed_gate: boolean | null;
    rubric_notes: string | null;
    private_notes: string | null;
    status: "draft" | "submitted" | null;
    updated_at: string | null;
    /** One entry per active rubric category, in display_order, hydrated with
     * the judge's own draft sub_scores when they exist. Always exactly the
     * number of active categories — missing line items show as null sub_score. */
    category_scores: CategorySubScore[];
  };
  /** The competition's configured rubric weights — judges aren't blind to the
   * weights, the spec calls for showing them as an eyebrow above the form. */
  rubric_weights: RubricWeightRow[];
  /** Judge id for the active competition assignment — cached for callers, but
   * the save_score_v2 RPC re-derives it server-side and is the source of truth. */
  judge_id: string;
};

type RawSubmissionFull = {
  id: string;
  file_url: string;
  file_name: string;
  file_size_bytes: number | null;
  submitted_at: string | null;
  team_id: string;
  deliverable_id: string;
  competition_id: string;
};

type RawDeliverable = {
  id: string;
  name: string;
  sequence: number;
  is_locked_gate: boolean;
  rubric: string | null;
  point_value: number | null;
  due_at: string | null;
  file_type: string | null;
};

type RawCompetition = {
  id: string;
  name: string;
  brief: string | null;
  opens_at: string | null;
  closes_at: string | null;
  companies: { name: string } | { name: string }[] | null;
};

type RawTeam = {
  id: string;
  name: string;
};

type RawTeamMember = {
  student_id: string;
  user_profiles: { full_name: string | null; avatar_url: string | null } | { full_name: string | null; avatar_url: string | null }[] | null;
};

type RawTeamSubmission = {
  id: string;
  deliverable_id: string;
  deliverables: { name: string; sequence: number } | { name: string; sequence: number }[] | null;
};

type RawScoreFull = {
  id: string;
  score: number | null;
  passed_gate: boolean | null;
  rubric_notes: string | null;
  private_notes: string | null;
  status: "draft" | "submitted" | null;
  updated_at: string | null;
};

type RawCategoryLineItem = {
  category_id: string;
  sub_score: number | null;
};

export async function getJudgeAssignment(
  competitionId: string,
  submissionId: string
): Promise<JudgeAssignmentDetail | null> {
  const { data: judgeData, error: judgeErr } = await supabase.rpc("judge_id_for", {
    comp_id: competitionId,
  });
  if (judgeErr || !judgeData) return null;
  const judgeId = String(judgeData);

  const { data: subRaw, error: subErr } = await supabase
    .from("submissions")
    .select("id, file_url, file_name, file_size_bytes, submitted_at, team_id, deliverable_id, competition_id")
    .eq("id", submissionId)
    .maybeSingle();
  if (subErr || !subRaw) return null;
  const sub = subRaw as RawSubmissionFull;

  const [
    { data: delRaw },
    { data: compRaw },
    { data: teamRaw },
    { data: membersRaw },
    { data: otherSubsRaw },
    { data: scoreRaw },
    { data: signedData },
    weights,
  ] = await Promise.all([
    supabase
      .from("deliverables")
      .select("id, name, sequence, is_locked_gate, rubric, point_value, due_at, file_type")
      .eq("id", sub.deliverable_id)
      .maybeSingle(),
    supabase
      .from("competitions")
      .select("id, name, brief, opens_at, closes_at, companies(name)")
      .eq("id", sub.competition_id)
      .maybeSingle(),
    supabase
      .from("teams")
      .select("id, name")
      .eq("id", sub.team_id)
      .maybeSingle(),
    supabase
      .from("team_members")
      .select("student_id, user_profiles(full_name, avatar_url)")
      .eq("team_id", sub.team_id),
    supabase
      .from("submissions")
      .select("id, deliverable_id, deliverables(name, sequence)")
      .eq("team_id", sub.team_id)
      .neq("id", submissionId),
    supabase
      .from("submission_scores")
      .select("id, score, passed_gate, rubric_notes, private_notes, status, updated_at")
      .eq("submission_id", submissionId)
      .eq("judge_id", judgeId)
      .maybeSingle(),
    supabase.storage.from("submissions").createSignedUrl(sub.file_url, 3600),
    getCompetitionRubricWeights(sub.competition_id),
  ]);

  const del = (delRaw as RawDeliverable | null) ?? null;
  const comp = (compRaw as RawCompetition | null) ?? null;
  const team = (teamRaw as RawTeam | null) ?? null;
  const score = (scoreRaw as RawScoreFull | null) ?? null;
  const company = comp ? unwrapOne(comp.companies) : null;

  // Hydrate per-category sub_scores. We start with one slot per active
  // category (so the form always has exactly the right number of inputs even
  // if no line items exist yet for a brand-new draft) and overlay any
  // existing line item values on top.
  let categoryScores: CategorySubScore[] = weights.map((w) => ({
    category_id: w.id,
    slug: w.slug,
    name: w.name,
    display_order: w.display_order,
    sub_score: null,
  }));

  if (score?.id) {
    const { data: lineItems } = await supabase
      .from("submission_score_categories")
      .select("category_id, sub_score")
      .eq("submission_score_id", score.id);
    const byCategory = new Map<string, number | null>();
    for (const li of (lineItems ?? []) as RawCategoryLineItem[]) {
      byCategory.set(li.category_id, li.sub_score);
    }
    categoryScores = categoryScores.map((c) => ({
      ...c,
      sub_score: byCategory.has(c.category_id) ? (byCategory.get(c.category_id) ?? null) : null,
    }));
  }

  return {
    submission: {
      id: sub.id,
      file_url: sub.file_url,
      file_name: sub.file_name,
      file_size_bytes: sub.file_size_bytes,
      submitted_at: sub.submitted_at,
      signed_url: signedData?.signedUrl ?? null,
    },
    team: {
      id: team?.id ?? sub.team_id,
      name: team?.name ?? "Team",
      members: ((membersRaw ?? []) as RawTeamMember[]).map((m) => {
        const up = unwrapOne(m.user_profiles);
        return {
          student_id: m.student_id,
          full_name: up?.full_name ?? null,
          avatar_url: up?.avatar_url ?? null,
        };
      }),
      other_submissions: ((otherSubsRaw ?? []) as RawTeamSubmission[])
        .map((row) => {
          const d = unwrapOne(row.deliverables);
          return {
            submission_id: row.id,
            deliverable_id: row.deliverable_id,
            deliverable_name: d?.name ?? "",
            deliverable_sequence: d?.sequence ?? 0,
          };
        })
        .sort((a, b) => a.deliverable_sequence - b.deliverable_sequence),
    },
    deliverable: {
      id: del?.id ?? sub.deliverable_id,
      name: del?.name ?? "",
      sequence: del?.sequence ?? 0,
      is_locked_gate: del?.is_locked_gate ?? false,
      rubric: del?.rubric ?? null,
      point_value: del?.point_value ?? null,
      due_at: del?.due_at ?? null,
      file_type: del?.file_type ?? null,
    },
    competition: {
      id: comp?.id ?? sub.competition_id,
      name: comp?.name ?? "Competition",
      brief: comp?.brief ?? null,
      opens_at: comp?.opens_at ?? null,
      closes_at: comp?.closes_at ?? null,
      company_name: company?.name ?? null,
    },
    myScore: {
      score: score?.score ?? null,
      passed_gate: score?.passed_gate ?? null,
      rubric_notes: score?.rubric_notes ?? null,
      private_notes: score?.private_notes ?? null,
      status: score?.status ?? null,
      updated_at: score?.updated_at ?? null,
      category_scores: categoryScores,
    },
    rubric_weights: weights,
    judge_id: judgeId,
  };
}

// ─── Draft + final score writes ────────────────────────────────────────

type ScorePayload = {
  competitionId: string;
  submissionId: string;
  passedGate: boolean | null;
  rubricNotes: string | null;
  privateNotes: string | null;
  /** One entry per active category. sub_score=null for unscored categories. */
  categoryScores: { category_id: string; sub_score: number | null }[];
};

type SaveScoreResult = {
  id: string;
  status: "draft" | "submitted";
  score: number | null;
  updated_at: string;
};

async function callSaveScoreV2(
  payload: ScorePayload,
  finalize: boolean
): Promise<{ data: SaveScoreResult | null; error: Error | null }> {
  const { data, error } = await supabase.rpc("save_score_v2", {
    p_submission_id: payload.submissionId,
    p_competition_id: payload.competitionId,
    p_passed_gate: payload.passedGate,
    p_rubric_notes: payload.rubricNotes,
    p_private_notes: payload.privateNotes,
    p_category_scores: payload.categoryScores.map((c) => ({
      category_id: c.category_id,
      sub_score: c.sub_score == null ? null : String(c.sub_score),
    })),
    p_finalize: finalize,
  });
  if (error) return { data: null, error: new Error(error.message) };
  return { data: data as SaveScoreResult, error: null };
}

export async function saveScoreDraft(
  payload: ScorePayload
): Promise<{ error: Error | null; updated_at: string | null; score: number | null }> {
  const { data, error } = await callSaveScoreV2(payload, false);
  return { error, updated_at: data?.updated_at ?? null, score: data?.score ?? null };
}

export async function submitScoreFinal(
  payload: ScorePayload
): Promise<{ error: Error | null; updated_at: string | null; score: number | null }> {
  const { data, error } = await callSaveScoreV2(payload, true);
  if (!error && data) {
    void import("./analytics").then(({ track }) =>
      track("judge_score_submitted", {
        submission_id: payload.submissionId,
        competition_id: payload.competitionId,
        score: data.score,
        passed_gate: payload.passedGate,
      })
    );
  }
  return { error, updated_at: data?.updated_at ?? null, score: data?.score ?? null };
}
